import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { calculateWeightLossKg, calculateWeightLossPercent } from "@/lib/ahtTreatmentPlanData";
import {
  AhtFoundayoReviewPanel,
  type FoundayoPanelState,
  type FoundayoPanelValues,
} from "./AhtFoundayoReviewPanel";
import { reviewRecords } from "@/features/aht-weight/reviewRecordStore";
import type { PlanReviewContext } from "@/features/aht-weight/planReviewContext";

export interface AHTSixMonthReviewFormData {
  selectedReviewPoint: string;
  reviewPointOther: string;
  selectedConsultMode: string;
  reviewDateVal: string;
  prescriberNameVal: string;
  prescriberRegVal: string;
  questionnaireOnTime: string;
  adherenceNotes: string;
  condMedChanges: string;
  adverseNotes: string;
  tolerabilityNotes: string;
  lifestyleNotes: string;
  currentWeightVal: string;
  currentBmiVal: string;
  fivePercentAchieved: string;
  stopRuleReconciliation: string;
  stopRuleDetail: string;
  treatmentJustification: string;
  weightVerified: string;
  weightDiscrepancy: string;
  weightDiscrepancyDetail: string;
  inclusionMet: string;
  inclusionDetail: string;
  newExclusion: string;
  newExclusionDetail: string;
  bmiFloor: string;
  bmiFloorAction: string;
  decision: string;
  ceaseReasons: string;
  ceaseDone: boolean;
  prescribingRationale: string;
  monitoringPlan: string;
  nextSixMonthDate: string;
  reviewFrequency: string;
  reviewInterval: string;
  reviewFreqReason: string;
  sigName: string;
  sigRegNo: string;
  sigText: string;
  sigDate: string;
  /** Foundayo (orforglipron) review — only present when Foundayo is the reviewed drug. */
  foundayo?: FoundayoPanelValues;
  foundayoStopReasons?: string[];
  foundayoReferrals?: string[];
  foundayoNeverRestart?: boolean;
  foundayoPercentChangeFromInitial?: number | null;
  maxToleratedDoseDate?: string | null;
}

interface AHTSixMonthReviewTemplateProps {
  isDarkMode: boolean;
  patientName?: string;
  patientDob?: string;
  drug?: string;
  currentDose?: string;
  brandBatch?: string;
  reviewPoint?: "6mo" | "12mo" | "18mo" | "24mo" | "other";
  reviewPointOther?: string;
  consultationMode?: "video" | "face_to_face";
  presciberName?: string;
  presciberRegNumber?: string;
  reviewDate?: string;
  baselineWeightKg?: number;
  baselineBmi?: number;
  currentWeightKg?: number;
  currentBmi?: number;
  initialSupplyDate?: string;
  adherenceNotesPrefill?: string;
  tolerabilityNotesPrefill?: string;
  currentWeightKgReported?: string;
  onComplete?: (data: AHTSixMonthReviewFormData) => void;
  loading?: boolean;
  /** Treatment plan this review belongs to. Needed to link the saved review to the plan. */
  planContext?: PlanReviewContext;
}

function SectionHeader({
  num,
  title,
  open,
  isDarkMode,
}: {
  num: number;
  title: string;
  open: boolean;
  isDarkMode: boolean;
}) {
  return (
    <CollapsibleTrigger className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left font-semibold text-sm transition-colors ${
      isDarkMode
        ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
        : "bg-gray-100 hover:bg-gray-200 text-gray-900"
    }`}>
      <span>{num}. {title}</span>
      {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
    </CollapsibleTrigger>
  );
}

function ReadOnlyField({ value, isDarkMode }: { value: string; isDarkMode: boolean }) {
  return (
    <div className={`px-3 py-2 text-sm rounded-lg border ${
      isDarkMode ? "bg-gray-800 border-gray-600 text-gray-300" : "bg-gray-50 border-gray-200 text-gray-700"
    }`}>
      {value || <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>—</span>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, isDarkMode, className = "" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; isDarkMode: boolean; className?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 text-sm rounded-lg border ${
        isDarkMode
          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
      } ${className}`}
    />
  );
}

function YesNoRow({
  label,
  value,
  onChange,
  condLabel,
  condValue,
  onCondChange,
  isDarkMode,
  redBorder,
  condIsTextarea,
  noCondLabel,
  noCondValue,
  onNoCondChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  condLabel?: string;
  condValue?: string;
  onCondChange?: (v: string) => void;
  isDarkMode: boolean;
  redBorder?: boolean;
  condIsTextarea?: boolean;
  noCondLabel?: string;
  noCondValue?: string;
  onNoCondChange?: (v: string) => void;
}) {
  const dm = isDarkMode;
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-4">
        <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>{label}</span>
        <div className="flex gap-3 shrink-0">
          {["Yes", "No"].map(opt => (
            <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name={`ynrow-${label}`} value={opt} checked={value === opt} onChange={() => onChange(opt)} className="accent-blue-600" />
              <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
            </label>
          ))}
        </div>
      </div>
      {value === "Yes" && condLabel && onCondChange && (
        <div className="ml-4">
          <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`}>{condLabel}</label>
          {condIsTextarea
            ? <textarea rows={2} value={condValue ?? ""} onChange={e => onCondChange(e.target.value)} className={`w-full px-3 py-2 text-sm rounded-lg border ${dm ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900"} ${redBorder ? "border-red-500" : ""}`} />
            : <TextInput value={condValue ?? ""} onChange={onCondChange} isDarkMode={dm} className={redBorder ? "border-red-500" : ""} />
          }
        </div>
      )}
      {value === "No" && noCondLabel && onNoCondChange && (
        <div className="ml-4">
          <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`}>{noCondLabel}</label>
          <TextInput value={noCondValue ?? ""} onChange={onNoCondChange} isDarkMode={dm} className={redBorder ? "border-red-500" : ""} />
        </div>
      )}
    </div>
  );
}

export function AHTSixMonthReviewTemplate({
  isDarkMode,
  patientName,
  patientDob,
  drug,
  currentDose,
  brandBatch,
  reviewPoint,
  reviewPointOther: reviewPointOtherProp,
  consultationMode,
  presciberName,
  presciberRegNumber,
  reviewDate,
  baselineWeightKg,
  baselineBmi,
  currentWeightKg,
  currentBmi,
  adherenceNotesPrefill,
  tolerabilityNotesPrefill,
  currentWeightKgReported,
  onComplete,
  loading,
  planContext,
}: AHTSixMonthReviewTemplateProps) {
  const today = new Date().toISOString().split("T")[0]!;
  const dm = isDarkMode;
  const isFoundayo = /foundayo|orforglipron/i.test(drug ?? "");
  const [foundayo, setFoundayo] = useState<FoundayoPanelState | null>(null);

  const [open, setOpen] = useState<Record<number, boolean>>({
    1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true, 8: true,
  });
  const toggle = (n: number) => setOpen(p => ({ ...p, [n]: !p[n] }));

  const wLossKg = baselineWeightKg != null && currentWeightKg != null
    ? calculateWeightLossKg(baselineWeightKg, currentWeightKg)
    : null;
  const wLossPct = baselineWeightKg != null && currentWeightKg != null
    ? calculateWeightLossPercent(baselineWeightKg, currentWeightKg)
    : null;
  const weightChangeDisplay = wLossKg != null && wLossPct != null
    ? `${wLossKg >= 0 ? "-" : "+"}${Math.abs(wLossKg).toFixed(1)} kg / ${wLossPct >= 0 ? "-" : "+"}${Math.abs(wLossPct).toFixed(1)}%`
    : "—";

  const [selectedReviewPoint, setSelectedReviewPoint] = useState<string>(reviewPoint ?? "");
  const [reviewPointOther, setReviewPointOther] = useState(reviewPointOtherProp ?? "");
  const [selectedConsultMode, setSelectedConsultMode] = useState(consultationMode ?? "");
  const [reviewDateVal, setReviewDateVal] = useState(reviewDate ?? today);
  const [prescriberNameVal, setPrescriberNameVal] = useState(presciberName ?? "");
  const [prescriberRegVal, setPrescriberRegVal] = useState(presciberRegNumber ?? "");
  const [questionnaireOnTime, setQuestionnaireOnTime] = useState("");

  const [adherenceNotes, setAdherenceNotes] = useState(adherenceNotesPrefill ?? "");
  const [condMedChanges, setCondMedChanges] = useState("");
  const [adverseNotes, setAdverseNotes] = useState("");
  const [tolerabilityNotes, setTolerabilityNotes] = useState(tolerabilityNotesPrefill ?? "");
  const [lifestyleNotes, setLifestyleNotes] = useState("");

  const [currentWeightVal, setCurrentWeightVal] = useState(currentWeightKgReported ?? currentWeightKg?.toString() ?? "");
  const [currentBmiVal, setCurrentBmiVal] = useState(currentBmi?.toString() ?? "");
  const [fivePercentAchieved, setFivePercentAchieved] = useState("");
  const [stopRuleReconciliation, setStopRuleReconciliation] = useState("");
  const [stopRuleDetail, setStopRuleDetail] = useState("");
  const [treatmentJustification, setTreatmentJustification] = useState("");

  const [weightVerified, setWeightVerified] = useState("");
  const [weightDiscrepancy, setWeightDiscrepancy] = useState("");
  const [weightDiscrepancyDetail, setWeightDiscrepancyDetail] = useState("");

  const [inclusionMet, setInclusionMet] = useState("");
  const [inclusionDetail, setInclusionDetail] = useState("");
  const [newExclusion, setNewExclusion] = useState("");
  const [newExclusionDetail, setNewExclusionDetail] = useState("");
  const [bmiFloor, setBmiFloor] = useState("");
  const [bmiFloorAction, setBmiFloorAction] = useState("");

  const [decision, setDecision] = useState("");
  const [ceaseReasons, setCeaseReasons] = useState("");
  const [ceaseDone, setCeaseDone] = useState(false);
  const [prescribingRationale, setPrescribingRationale] = useState("");

  const [monitoringPlan, setMonitoringPlan] = useState("");
  const [nextSixMonthDate, setNextSixMonthDate] = useState("");
  const [reviewFrequency, setReviewFrequency] = useState("");
  const [reviewInterval, setReviewInterval] = useState("");
  const [reviewFreqReason, setReviewFreqReason] = useState("");

  const [sigName, setSigName] = useState(presciberName ?? "");
  const [sigRegNo, setSigRegNo] = useState(presciberRegNumber ?? "");
  const [sigText, setSigText] = useState("");
  const [sigDate, setSigDate] = useState(today);

  const cardClass = `rounded-lg border ${dm ? "border-gray-700" : "border-gray-200"}`;
  const inputClass = `w-full px-3 py-2 text-sm rounded-lg border ${dm ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`;
  const labelClass = `block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`;
  const sectionBodyClass = `p-4 space-y-4 ${dm ? "bg-gray-800" : "bg-white"}`;
  const reviewPoints = [
    { key: "6mo", label: "6 mo" },
    { key: "12mo", label: "12 mo" },
    { key: "18mo", label: "18 mo" },
    { key: "24mo", label: "24 mo" },
    { key: "other", label: "Other" },
  ];

  // Foundayo decides from its own panel; every other drug uses the Section 6 decision.
  const effectiveDecision = isFoundayo ? (foundayo?.values.doseDecision ?? "") : decision;
  const foundayoBlockers = isFoundayo
    ? foundayo
      ? foundayo.saveBlockers
      : ["Complete the Foundayo review section."]
    : [];

  const handleSubmit = () => {
    if (!onComplete) return;
    if (isFoundayo && foundayo && planContext) {
      // Append-only audit record linked to the plan and its latest supply event. It is also what the
      // never-restart lookup reads, so a confirmed-pancreatitis review blocks Foundayo at selection.
      reviewRecords.append({
        planId: planContext.planId,
        supplyEventId: planContext.supplyEventId,
        patientId: planContext.patientId,
        kind: "6month",
        drugId: "foundayo",
        data: {
          reviewDate: reviewDateVal,
          selectedReviewPoint,
          foundayo: foundayo.values,
          foundayoStopReasons: foundayo.result.stopReasons,
          foundayoReferrals: foundayo.result.referrals,
          foundayoNeverRestart: foundayo.result.neverRestart,
          foundayoPercentChangeFromInitial: foundayo.result.percentChangeFromInitial,
          waistDate: foundayo.values.waistCm ? today : undefined,
          maxToleratedDoseDate: foundayo.maxToleratedDoseDate ?? undefined,
          sigName,
          sigRegNo,
          sigText,
          sigDate,
        },
      });
    }
    onComplete({
      selectedReviewPoint,
      reviewPointOther,
      selectedConsultMode,
      reviewDateVal,
      prescriberNameVal,
      prescriberRegVal,
      questionnaireOnTime,
      adherenceNotes,
      condMedChanges,
      adverseNotes,
      tolerabilityNotes,
      lifestyleNotes,
      currentWeightVal,
      currentBmiVal,
      fivePercentAchieved,
      stopRuleReconciliation,
      stopRuleDetail,
      treatmentJustification,
      weightVerified,
      weightDiscrepancy,
      weightDiscrepancyDetail,
      inclusionMet,
      inclusionDetail,
      newExclusion,
      newExclusionDetail,
      bmiFloor,
      bmiFloorAction,
      decision: effectiveDecision,
      ceaseReasons,
      ceaseDone,
      prescribingRationale,
      monitoringPlan,
      nextSixMonthDate,
      reviewFrequency,
      reviewInterval,
      reviewFreqReason,
      sigName,
      sigRegNo,
      sigText,
      sigDate,
      ...(isFoundayo && foundayo
        ? {
            foundayo: foundayo.values,
            foundayoStopReasons: foundayo.result.stopReasons,
            foundayoReferrals: foundayo.result.referrals,
            foundayoNeverRestart: foundayo.result.neverRestart,
            foundayoPercentChangeFromInitial: foundayo.result.percentChangeFromInitial,
            maxToleratedDoseDate: foundayo.maxToleratedDoseDate,
          }
        : {}),
    });
  };

  return (
    <div className={`space-y-3 text-sm ${dm ? "text-gray-200" : "text-gray-900"}`}>

      {/* Title */}
      <div className={`px-4 py-3 rounded-lg border ${dm ? "bg-purple-900/20 border-purple-700" : "bg-purple-50 border-purple-200"}`}>
        <p className={`font-semibold ${dm ? "text-purple-200" : "text-purple-900"}`}>
          AHT Weight Management — 6-Month Independent Review (Section B)
        </p>
        <p className={`text-xs mt-0.5 ${dm ? "text-purple-300" : "text-purple-700"}`}>
          Allied Health Training Limited · AHT-WM-6MO-v1
        </p>
      </div>

      {/* Section 1 */}
      <Collapsible open={open[1]} onOpenChange={() => toggle(1)} className={cardClass}>
        <SectionHeader num={1} title="Patient & review details" open={!!open[1]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Patient name</label>
                <ReadOnlyField value={patientName ?? ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Date of birth</label>
                <ReadOnlyField value={patientDob ?? ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Medicine & current dose</label>
                <ReadOnlyField value={[drug, currentDose].filter(Boolean).join(" — ")} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Brand / batch</label>
                <ReadOnlyField value={brandBatch ?? ""} isDarkMode={dm} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Review point</label>
              <div className="flex flex-wrap gap-4 mt-1">
                {reviewPoints.map(rp => (
                  <label key={rp.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReviewPoint === rp.key}
                      onChange={() => setSelectedReviewPoint(prev => prev === rp.key ? "" : rp.key)}
                      className="accent-blue-600"
                    />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{rp.label}</span>
                  </label>
                ))}
              </div>
              {selectedReviewPoint === "other" && (
                <div className="mt-2">
                  <TextInput value={reviewPointOther} onChange={setReviewPointOther} placeholder="Specify review point..." isDarkMode={dm} />
                </div>
              )}
            </div>

            <div>
              <label className={labelClass}>Consultation mode</label>
              <div className="flex gap-4 mt-1">
                {[{ key: "video", label: "Video" }, { key: "face_to_face", label: "Face-to-face" }].map(m => (
                  <label key={m.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="consultMode" value={m.key} checked={selectedConsultMode === m.key} onChange={() => setSelectedConsultMode(m.key)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{m.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Date of review</label>
                <input type="date" value={reviewDateVal} onChange={e => setReviewDateVal(e.target.value)} className={inputClass} />
              </div>
              <div />
              <div>
                <label className={labelClass}>Prescriber name</label>
                <TextInput value={prescriberNameVal} onChange={setPrescriberNameVal} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Registration no.</label>
                <TextInput value={prescriberRegVal} onChange={setPrescriberRegVal} isDarkMode={dm} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>
                  Questionnaire returned & consultation attended on time?
                </span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="questionnaireOnTime" value={opt} checked={questionnaireOnTime === opt} onChange={() => setQuestionnaireOnTime(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {questionnaireOnTime === "No" && (
                <div className={`px-3 py-2 rounded-lg border text-sm font-medium ${dm ? "bg-red-900/20 border-red-700 text-red-300" : "bg-red-50 border-red-300 text-red-800"}`}>
                  Treatment not to continue (per Step B1)
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 2 */}
      <Collapsible open={open[2]} onOpenChange={() => toggle(2)} className={cardClass}>
        <SectionHeader num={2} title="Interval review (since last review)" open={!!open[2]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div>
              <label className={labelClass}>Missed doses / adherence</label>
              <textarea rows={2} value={adherenceNotes} onChange={e => setAdherenceNotes(e.target.value)} placeholder="Any missed doses, injection technique concerns, adherence issues..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Changes to medical conditions / medication / OTC since initiation</label>
              <textarea rows={2} value={condMedChanges} onChange={e => setCondMedChanges(e.target.value)} placeholder="New diagnoses, medication changes, relevant OTC use..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Adverse effects over the period (and how managed)</label>
              <textarea rows={2} value={adverseNotes} onChange={e => setAdverseNotes(e.target.value)} placeholder="GI effects, injection site reactions, other adverse events and management..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Tolerability, sleep, mood, wellbeing</label>
              <textarea rows={2} value={tolerabilityNotes} onChange={e => setTolerabilityNotes(e.target.value)} placeholder="Patient-reported quality of life, sleep, mood changes..." className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Lifestyle — diet, physical activity, behavioural engagement</label>
              <textarea rows={2} value={lifestyleNotes} onChange={e => setLifestyleNotes(e.target.value)} placeholder="Dietary changes, exercise habits, programme engagement..." className={inputClass} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 3 */}
      <Collapsible open={open[3]} onOpenChange={() => toggle(3)} className={cardClass}>
        <SectionHeader num={3} title="Cumulative progress & efficacy" open={!!open[3]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <p className={`text-xs italic ${dm ? "text-gray-400" : "text-gray-500"}`}>
              This is the independent progress check — use cumulative figures, not just since the last cycle.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Baseline weight (kg)</label>
                <ReadOnlyField value={baselineWeightKg != null ? `${baselineWeightKg} kg` : ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Baseline BMI</label>
                <ReadOnlyField value={baselineBmi != null ? `${baselineBmi} kg/m²` : ""} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Current weight (verified at consultation) kg</label>
                <TextInput value={currentWeightVal} onChange={setCurrentWeightVal} placeholder="e.g. 94.2" isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Current BMI (kg/m²)</label>
                <TextInput value={currentBmiVal} onChange={setCurrentBmiVal} placeholder="e.g. 31.3" isDarkMode={dm} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Total weight change (kg) and % of baseline weight — system calculated</label>
              <ReadOnlyField value={weightChangeDisplay} isDarkMode={dm} />
            </div>

            <YesNoRow
              label="≥5% weight loss achieved at maximum tolerated dose?"
              value={fivePercentAchieved}
              onChange={setFivePercentAchieved}
              isDarkMode={dm}
            />
            {fivePercentAchieved === "No" && (
              <div className={`px-3 py-2 rounded-lg border text-sm ${dm ? "bg-amber-900/20 border-amber-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                Drug-specific stop rule may apply — review Section 3 reconciliation below
              </div>
            )}

            <div className="space-y-2">
              <label className={labelClass}>Drug-specific efficacy stop-rule reconciliation (semaglutide/tirzepatide/Foundayo 6-mo; liraglutide & orlistat 12-wk) — met?</label>
              <div className="flex gap-4">
                {["Met", "Not met"].map(opt => (
                  <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="stopRuleReconciliation" value={opt} checked={stopRuleReconciliation === opt} onChange={() => setStopRuleReconciliation(opt)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                  </label>
                ))}
              </div>
              {stopRuleReconciliation === "Not met" && (
                <div className="ml-4">
                  <label className={labelClass}>Detail:</label>
                  <TextInput value={stopRuleDetail} onChange={setStopRuleDetail} isDarkMode={dm} />
                </div>
              )}
            </div>

            {isFoundayo && (
              <AhtFoundayoReviewPanel
                isDarkMode={dm}
                initialWeightKg={planContext?.initialWeightKg ?? baselineWeightKg ?? null}
                lastWaistDate={planContext ? ((reviewRecords.latestValue(planContext.planId, "waistDate") as string | undefined) ?? null) : null}
                priorMaxToleratedDoseDate={planContext ? ((reviewRecords.latestValue(planContext.planId, "maxToleratedDoseDate") as string | undefined) ?? null) : null}
                onChange={setFoundayo}
              />
            )}

            <div>
              <label className={labelClass}>Progress against the patient's own goal / is continued treatment clinically justified?</label>
              <textarea rows={3} value={treatmentJustification} onChange={e => setTreatmentJustification(e.target.value)} placeholder="Document progress against agreed goals and clinical justification for continuation..." className={inputClass} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 4 */}
      <Collapsible open={open[4]} onOpenChange={() => toggle(4)} className={cardClass}>
        <SectionHeader num={4} title="Independent verification" open={!!open[4]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <YesNoRow
              label="Weight verified at consultation (observed / scale photo)?"
              value={weightVerified}
              onChange={setWeightVerified}
              isDarkMode={dm}
            />
            <YesNoRow
              label="Any discrepancy between self-reported and verified weight?"
              value={weightDiscrepancy}
              onChange={setWeightDiscrepancy}
              condLabel="Detail:"
              condValue={weightDiscrepancyDetail}
              onCondChange={setWeightDiscrepancyDetail}
              isDarkMode={dm}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 5 */}
      <Collapsible open={open[5]} onOpenChange={() => toggle(5)} className={cardClass}>
        <SectionHeader num={5} title="Ongoing suitability" open={!!open[5]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Patient still meets inclusion criteria?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="inclusionMet" value={opt} checked={inclusionMet === opt} onChange={() => setInclusionMet(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {inclusionMet === "No" && (
                <div className="ml-4">
                  <label className={labelClass}>Detail:</label>
                  <TextInput value={inclusionDetail} onChange={setInclusionDetail} isDarkMode={dm} className="border-red-500" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>Any new exclusion / contraindication?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="newExclusion" value={opt} checked={newExclusion === opt} onChange={() => setNewExclusion(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {newExclusion === "Yes" && (
                <div className="ml-4">
                  <label className={labelClass}>Detail:</label>
                  <TextInput value={newExclusionDetail} onChange={setNewExclusionDetail} isDarkMode={dm} className="border-red-500" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-4">
                <span className={`text-sm flex-1 ${dm ? "text-gray-200" : "text-gray-800"}`}>BMI floor reached (continuation review)?</span>
                <div className="flex gap-3 shrink-0">
                  {["Yes", "No"].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="bmiFloor" value={opt} checked={bmiFloor === opt} onChange={() => setBmiFloor(opt)} className="accent-blue-600" />
                      <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {bmiFloor === "Yes" && (
                <div className="ml-4">
                  <label className={labelClass}>Action:</label>
                  <TextInput value={bmiFloorAction} onChange={setBmiFloorAction} isDarkMode={dm} />
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 6 */}
      <Collapsible open={open[6]} onOpenChange={() => toggle(6)} className={cardClass}>
        <SectionHeader num={6} title="Decision" open={!!open[6]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div>
              <label className={labelClass}>Decision</label>
              <div className="flex flex-wrap gap-4 mt-1">
                {["Continue same dose", "Escalate", "Maintain", "Cease / not to prescribe"].map(opt => (
                  <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="decision" value={opt} checked={decision === opt} onChange={() => setDecision(opt)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            {decision === "Cease / not to prescribe" && (
              <div className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`}>
                    Reasons for ceasing / not prescribing (mandatory):
                  </label>
                  <textarea
                    rows={3}
                    value={ceaseReasons}
                    onChange={e => setCeaseReasons(e.target.value)}
                    className={`w-full px-3 py-2 text-sm rounded-lg border-red-500 border ${dm ? "bg-gray-700 text-gray-100 placeholder-gray-500" : "bg-white text-gray-900"}`}
                    placeholder="Document reasons for ceasing treatment..."
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={ceaseDone} onChange={e => setCeaseDone(e.target.checked)} className="accent-blue-600" />
                  <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>
                    Signpost to patient's regular prescriber and document ☐ Done
                  </span>
                </label>
              </div>
            )}

            <div>
              <label className={labelClass}>Reasons for the prescribing decision (incl. medicine & dose where appropriate)</label>
              <textarea rows={3} value={prescribingRationale} onChange={e => setPrescribingRationale(e.target.value)} placeholder="Clinical reasoning for the prescribing decision..." className={inputClass} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 7 */}
      <Collapsible open={open[7]} onOpenChange={() => toggle(7)} className={cardClass}>
        <SectionHeader num={7} title="Monitoring & follow-up plan" open={!!open[7]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div>
              <label className={labelClass}>Monitoring / dose-escalation plan communicated to patient</label>
              <textarea rows={3} value={monitoringPlan} onChange={e => setMonitoringPlan(e.target.value)} placeholder="Document monitoring plan and any dose escalation schedule communicated..." className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Next 6-month review date</label>
              <input type="date" value={nextSixMonthDate} onChange={e => setNextSixMonthDate(e.target.value)} className={inputClass} />
            </div>

            <div className="space-y-2">
              <label className={labelClass}>Review frequency decision (Step B7): can reviews remain 6-monthly, or more frequent for this patient?</label>
              <div className="flex gap-4">
                {["Remain 6-monthly", "More frequent"].map(opt => (
                  <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="reviewFrequency" value={opt} checked={reviewFrequency === opt} onChange={() => setReviewFrequency(opt)} className="accent-blue-600" />
                    <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt}</span>
                  </label>
                ))}
              </div>
              {reviewFrequency === "More frequent" && (
                <div className="grid grid-cols-2 gap-3 ml-4">
                  <div>
                    <label className={labelClass}>Interval:</label>
                    <TextInput value={reviewInterval} onChange={setReviewInterval} placeholder="e.g. every 4 weeks" isDarkMode={dm} />
                  </div>
                  <div>
                    <label className={labelClass}>Reason:</label>
                    <TextInput value={reviewFreqReason} onChange={setReviewFreqReason} placeholder="Clinical reason..." isDarkMode={dm} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section 8 */}
      <Collapsible open={open[8]} onOpenChange={() => toggle(8)} className={cardClass}>
        <SectionHeader num={8} title="Prescriber sign-off" open={!!open[8]} isDarkMode={dm} />
        <CollapsibleContent>
          <div className={sectionBodyClass}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Prescriber name</label>
                <TextInput value={sigName} onChange={setSigName} isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Registration no.</label>
                <TextInput value={sigRegNo} onChange={setSigRegNo} isDarkMode={dm} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Signature</label>
                <TextInput value={sigText} onChange={setSigText} placeholder="Type name as signature" isDarkMode={dm} />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input type="date" value={sigDate} onChange={e => setSigDate(e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Footer */}
      <p className={`text-center text-xs py-2 ${dm ? "text-gray-500" : "text-gray-400"}`}>
        Retain for 8 years · subject to internal and external audit · Allied Health Training Limited
      </p>

      {/* Submit */}
      {foundayoBlockers.length > 0 && (
        <ul
          className={`list-disc pl-6 text-xs ${dm ? "text-amber-300" : "text-amber-800"}`}
          data-testid="review-save-problems"
        >
          {foundayoBlockers.map(b => <li key={b}>{b}</li>)}
        </ul>
      )}
      <Button
        onClick={handleSubmit}
        disabled={loading || !effectiveDecision || !sigText || foundayoBlockers.length > 0}
        className="w-full mt-4"
      >
        {loading ? "Saving..." : "Complete 6-Month Review"}
      </Button>
    </div>
  );
}
